import {toast} from '@wandb/weave/common/components/elements/Toast';
import WandbLoader from '@wandb/weave/common/components/WandbLoader';
import {
  saveTableAsCSV,
  type Table,
  type TableRow,
} from '@wandb/weave/common/util/csv';
import {Button} from '@wandb/weave/components';
import React, {useMemo, useState} from 'react';

import {UsageType} from '../../generated/graphql';
import {compareAsc, secondsToHoursExact} from '../../util/date';
import {useRampFlagInferenceCachedUncachedCSV} from '../../util/rampFeatureFlags/rampFeatureFlags';
import {bytesInGB, bytesInMB} from '../../util/storage';
import {
  normalizeUsageTotalBigInt,
  type UsageAggregationTypes,
  valueAggregator,
} from '../../util/usage/utils';
import {
  getIntervalMarkers,
  TimeRange,
  useHistoricUsageChartContext,
} from './HistoricUsageChartContextProvider';

export const CSV_MAX_TIME_RANGE = TimeRange.LAST_24_MONTHS;

function getUnitColumnLabel(usageType: UsageType): string {
  switch (usageType) {
    case UsageType.Storage:
      return 'Storage (average GB)';
    case UsageType.TrackedHours:
      return 'Tracked hours';
    case UsageType.Weave:
      return 'Weave (MB ingested)';
    case UsageType.InferenceCost:
      return 'Inference ($ credits used)';
    case UsageType.TrainingCost:
      return 'Training ($ credits used)';
    case UsageType.WbAgentCost:
      return 'WB Agent ($ credits used)';
    case UsageType.TrainingDuration:
      return 'Training (ms used)';
    case UsageType.UserSeats:
      return 'User seats';
    default:
      console.log('Unknown usage type: ', usageType);
      return 'Unknown';
  }
}

export function getValueFormatterWithoutUnits(
  usageType: UsageType
): (value: number) => string {
  switch (usageType) {
    case UsageType.Storage:
      return value => `${value / bytesInGB}`;
    case UsageType.Weave:
      return value => `${value / bytesInMB}`;
    case UsageType.TrackedHours:
      return value => `${secondsToHoursExact(value)}`;
    case UsageType.InferenceCost:
      return value => `$${(value / 100).toFixed(2)}`;
    case UsageType.TrainingCost:
    case UsageType.WbAgentCost:
      return value => `$${(value / 100).toFixed(2)}`;
    case UsageType.TrainingDuration:
      return value => `${value}`;
    case UsageType.UserSeats:
      return value => `${value}`;
    default:
      console.log('Unknown usage type: ', usageType);
      return value => `${value}`;
  }
}

enum DefaultColumn {
  START = 'Start',
  END = 'End',
  TEAM = 'Team',
  PROJECT = 'Project',
}

export function processData(
  usage: UsageAggregationTypes[],
  usageType: UsageType,
  aggregationCutoffDate: Date | undefined,
  showCachedUncached: boolean = false
): Table {
  const filteredData = usage
    ?.sort(
      (
        {start: start1, entityName: entityName1, projectName: projectName1},
        {start: start2, entityName: entityName2, projectName: projectName2}
      ) => {
        const dateSort = compareAsc(new Date(start1), new Date(start2));
        if (dateSort !== 0) {
          return dateSort;
        }
        if (entityName1 !== entityName2) {
          return entityName1.localeCompare(entityName2);
        }
        return projectName1.localeCompare(projectName2);
      }
    )
    ?.map(usageData => {
      if (usageData.__typename === 'InferenceCostUsageAggregation') {
        return {
          ...usageData,
          value: normalizeUsageTotalBigInt(usageData.value, usageType),
          inputCount: Number(usageData.inputCount),
          cachedCount: Number(usageData.cachedCount),
          uncachedCount: Number(usageData.uncachedCount),
          outputCount: Number(usageData.outputCount),
          inputCost: normalizeUsageTotalBigInt(usageData.inputCost, usageType),
          cachedCost: normalizeUsageTotalBigInt(
            usageData.cachedCost,
            usageType
          ),
          uncachedCost: normalizeUsageTotalBigInt(
            usageData.uncachedCost,
            usageType
          ),
          outputCost: normalizeUsageTotalBigInt(
            usageData.outputCost,
            usageType
          ),
        };
      }
      return {
        ...usageData,
        value: normalizeUsageTotalBigInt(usageData.value, usageType),
      };
    })
    ?.map(usageData => ({
      ...usageData,
      // need to divide storage by number of days in the month, so use the valueAggregator
      value: valueAggregator(usageData, usageType, 0, aggregationCutoffDate),
    }));
  const valueFormatter = getValueFormatterWithoutUnits(usageType);

  const InferenceColumns = {
    Endpoint: 'Endpoint',
    ...(showCachedUncached
      ? {
          CachedTokens: 'Cached Input Tokens',
          UncachedTokens: 'Uncached Input Tokens',
        }
      : {InputTokens: 'Input Tokens'}),
    OutputTokens: 'Output Tokens',
    ...(showCachedUncached
      ? {
          CachedUsage: 'Cached Input Usage',
          UncachedUsage: 'Uncached Input Usage',
        }
      : {InputUsage: 'Input Usage'}),
    OutputUsage: 'Output Usage',
  };

  let additionalValues = {} as Record<string, string>;

  if (usageType === UsageType.InferenceCost) {
    additionalValues = {
      ...InferenceColumns,
    };
  }

  // Convert it to CSV format
  return (
    filteredData?.reduce(
      (table, data) => {
        const {start, end, entityName, projectName, value} = data;
        const row = {} as TableRow;
        row[DefaultColumn.START] = new Intl.DateTimeFormat('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          timeZone: 'UTC',
        }).format(new Date(start));
        row[DefaultColumn.END] = new Intl.DateTimeFormat('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          timeZone: 'UTC',
        }).format(new Date(end));
        row[DefaultColumn.TEAM] = entityName;
        row[DefaultColumn.PROJECT] = projectName;
        // This __typename check is actually performing type coercion
        // so that TypeScript knows that data is of type InferenceCostUsageAggregation
        // and not just UsageAggregation.
        // https://chatgpt.com/share/e/6850536b-1894-800e-b089-e81670bbc1c5
        if (data.__typename === 'InferenceCostUsageAggregation') {
          row['Endpoint'] = data.endpointName;
          if (showCachedUncached) {
            row['Cached Input Tokens'] = data.cachedCount;
            row['Uncached Input Tokens'] = data.uncachedCount;
          } else {
            row['Input Tokens'] = data.inputCount;
          }
          row['Output Tokens'] = data.outputCount;
          if (showCachedUncached) {
            row['Cached Input Usage'] = valueFormatter(data.cachedCost);
            row['Uncached Input Usage'] = valueFormatter(data.uncachedCost);
          } else {
            row['Input Usage'] = valueFormatter(data.inputCost);
          }
          row['Output Usage'] = valueFormatter(data.outputCost);
        }
        row[getUnitColumnLabel(usageType)] = valueFormatter(value);
        table.data.push(row);
        return table;
      },
      {
        data: [] as TableRow[],
        cols: [
          ...Object.values(DefaultColumn),
          ...Object.values(additionalValues),
          getUnitColumnLabel(usageType),
        ],
      }
    ) ?? {
      data: [] as TableRow[],
      cols: [
        ...Object.values(DefaultColumn),
        ...Object.values(additionalValues),
        getUnitColumnLabel(usageType),
      ],
    }
  );
}

// remove iconOnly prop (will always show) when team usage charts are released
export const ExportAsCSV = ({iconOnly = false}: {iconOnly?: boolean} = {}) => {
  const {usageType, processedData, fetchCSVUsage, aggregationCutoffDate} =
    useHistoricUsageChartContext();
  const showCachedUncachedColumns = useRampFlagInferenceCachedUncachedCSV();
  const intervalMarkers = getIntervalMarkers(CSV_MAX_TIME_RANGE);
  const [isLoading, setIsLoading] = useState(false);
  const [usageData, setUsageData] = useState<Partial<
    Record<UsageType, UsageAggregationTypes[]>
  > | null>(null);
  const data = usageData?.[usageType];

  // Can't use Apollo useLazyQuery because
  // 1. Apollo's cache is super slow when dealing with large amounts of data. But we don't need the cache anyway because the user is unlikely to download the same data twice
  // 2. There's a bug with useLazyQuery where it acts like useQuery after the first render. This would mean that if a user clicks Export CSV on the Storage page, the data is queried
  //    as expected. But then, if they go to the Tracked hours tab, the data is automatically queried on render instead of only when they click.
  //    This solution using the apollo client directly is found here https://github.com/apollographql/apollo-client/issues/7484#issuecomment-1101724093.
  // 3. On top of that, React doesn't seem to realize when switching between the tabs that this component should unmount and remount. So, any
  //    local state is still kept. That's why we need `usageData` to be a Record, instead of just straightforward UsageTabInfoQuery.
  const getUsage = async () =>
    await fetchCSVUsage(usageType, intervalMarkers)
      .then(fetchedUsage => {
        setUsageData(prevData => {
          const newData = {...prevData};
          newData[usageType] = fetchedUsage;
          return newData;
        });
        setIsLoading(false);
        saveTableAsCSV(
          processData(
            fetchedUsage,
            usageType,
            aggregationCutoffDate,
            showCachedUncachedColumns
          )
        );
      })
      .catch(error => {
        toast(`Error exporting data as CSV: ${error}`, {
          type: 'error',
        });
        setIsLoading(false);
      });

  const processedCSVData: Table = useMemo(
    () =>
      data != null
        ? processData(
            data,
            usageType,
            aggregationCutoffDate,
            showCachedUncachedColumns
          )
        : {
            cols: [
              ...Object.values(DefaultColumn),
              getUnitColumnLabel(usageType),
            ],
            data: [],
          },
    [aggregationCutoffDate, data, usageType, showCachedUncachedColumns]
  );

  // If there's no data visible in the chart, that means there's nothing to export
  const hasNoDataToDownload = processedData?.every(({hideValue}) => hideValue);
  return (
    <Button
      data-test="export-as-csv"
      icon="download"
      disabled={hasNoDataToDownload}
      variant="ghost"
      size="medium"
      onClick={async () => {
        if (data == null) {
          setIsLoading(true);
          await getUsage();
        } else {
          setIsLoading(false);
          saveTableAsCSV(processedCSVData);
        }
      }}>
      <>
        {!iconOnly && 'Export as CSV'}
        {isLoading && (
          <div className="ml-8 flex items-center">
            <WandbLoader active size="tiny" inline />
          </div>
        )}
      </>
    </Button>
  );
};
